import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Images } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { api, imgSrc, errMsg, prepareImage } from "../../lib/api";
import Uploader from "./Uploader";
import LibraryPicker from "./LibraryPicker";

const BLANK = {
  title: "",
  year: "",
  medium: "",
  dimensions: "",
  price: "",
  availability: "original",
  featured: false,
};

export default function ArtworkFields({ artwork, library, onDone, onCancel }) {
  const editing = !!artwork;
  const [form, setForm] = useState(BLANK);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [libPath, setLibPath] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (artwork)
      setForm({
        title: artwork.title || "",
        year: artwork.year || "",
        medium: artwork.medium || "",
        dimensions: artwork.dimensions || "",
        price: artwork.price ?? "",
        availability: artwork.availability || "original",
        featured: !!artwork.featured,
      });
    else setForm(BLANK);
    setLibPath("");
    setPickerOpen(false);
  }, [artwork]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const pick = async (f) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Choose an image file");
      return;
    }
    if (f.size > 25 * 1024 * 1024) {
      toast.error("Image is larger than 25 MB");
      return;
    }
    const prepared = await prepareImage(f);
    setFile(prepared);
    setPreview(URL.createObjectURL(prepared));
    setLibPath("");
  };

  const clearPhoto = () => {
    setFile(null);
    setPreview(null);
  };

  const pickFromLibrary = (p) => {
    setLibPath(p.storage_path);
    clearPhoto();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!editing && !file && !libPath) {
      toast.error("Choose a photo — add one from your library or upload one");
      return;
    }
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/artworks/${artwork.id}`, {
          ...form,
          price: form.price === "" ? null : Number(form.price),
        });
        if (file) {
          const fd = new FormData();
          fd.append("file", file);
          await api.put(`/artworks/${artwork.id}/photo`, fd);
        } else if (libPath) {
          await api.put(`/artworks/${artwork.id}/photo`, { image_path: libPath });
        }
        toast.success("Changes saved");
      } else {
        const fd = new FormData();
        if (file) fd.append("file", file);
        else fd.append("image_path", libPath);
        fd.append("title", form.title);
        if (form.year) fd.append("year", form.year);
        if (form.medium) fd.append("medium", form.medium);
        if (form.dimensions) fd.append("dimensions", form.dimensions);
        if (form.price !== "") fd.append("price", String(form.price));
        fd.append("availability", form.availability);
        fd.append("featured", form.featured ? "true" : "false");
        await api.post("/artworks", fd);
        toast.success("Added to the collection");
        clearPhoto();
        setLibPath("");
        setForm(BLANK);
      }
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form id="artwork-form" onSubmit={submit} data-testid="artwork-form" className="border border-line bg-panel p-6 md:p-8">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="eyebrow !text-gold/90">{editing ? "Edit Work" : "Add a Work"}</p>
          <h2 className="font-display text-2xl mt-2">{editing ? artwork.title : "New piece"}</h2>
        </div>
        {editing && (
          <button
            type="button"
            onClick={onCancel}
            data-testid="artwork-form-cancel-button"
            className="font-meta text-[10px] uppercase tracking-[0.25em] text-mute hover:text-bone border border-line px-4 py-2"
          >
            Cancel
          </button>
        )}
      </div>

      <div className="mt-6">
        <Uploader
          file={file}
          preview={preview || (libPath ? imgSrc(libPath) : null)}
          onPick={pick}
          onClear={() => {
            clearPhoto();
            setLibPath("");
          }}
        />
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            data-testid="library-open-picker-button"
            className="flex items-center gap-2 border border-white/20 px-5 py-3 font-meta text-[10px] uppercase tracking-[0.25em] text-bone/85 hover:text-ochre hover:border-ochre/50 transition-colors duration-300"
          >
            <Images size={13} /> Add image from library
          </button>
          {libPath && !preview && (
            <span className="font-meta text-[9px] uppercase tracking-[0.25em] text-ochre" data-testid="library-selected-note">
              Library photo selected — drop a file above to override
            </span>
          )}
          {editing && !libPath && !preview && (
            <span className="font-meta text-[9px] uppercase tracking-[0.25em] text-mute">
              Adding a new image replaces the current photo
            </span>
          )}
        </div>
      </div>

      <LibraryPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        library={library}
        selectedPath={libPath}
        onSelect={(p) => {
          pickFromLibrary(p);
          setPickerOpen(false);
          toast.success("Image selected from your library");
        }}
      />

      <div className="mt-6 space-y-5">
        <div>
          <label className="field-label" htmlFor="art-title">Title *</label>
          <input
            id="art-title"
            required
            value={form.title}
            onChange={(e) => set("title")(e.target.value)}
            placeholder="e.g. Study in Amber"
            className="field-input"
            data-testid="artwork-title-input"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="field-label" htmlFor="art-year">Year</label>
            <input
              id="art-year"
              value={form.year}
              onChange={(e) => set("year")(e.target.value)}
              placeholder="2026"
              className="field-input"
              data-testid="artwork-year-input"
            />
          </div>
          <div>
            <label className="field-label" htmlFor="art-price">Price (USD)</label>
            <input
              id="art-price"
              type="number"
              min="0"
              step="1"
              value={form.price}
              onChange={(e) => set("price")(e.target.value)}
              placeholder="2400"
              className="field-input"
              data-testid="artwork-price-input"
            />
          </div>
        </div>
        <div>
          <label className="field-label" htmlFor="art-medium">Medium</label>
          <input
            id="art-medium"
            value={form.medium}
            onChange={(e) => set("medium")(e.target.value)}
            placeholder="Oil on linen"
            className="field-input"
            data-testid="artwork-medium-input"
          />
        </div>
        <div>
          <label className="field-label" htmlFor="art-dimensions">Dimensions</label>
          <input
            id="art-dimensions"
            value={form.dimensions}
            onChange={(e) => set("dimensions")(e.target.value)}
            placeholder="48 × 60 in / 122 × 152 cm"
            className="field-input"
            data-testid="artwork-dimensions-input"
          />
        </div>
        <div>
          <label className="field-label">Availability</label>
          <Select value={form.availability} onValueChange={set("availability")}>
            <SelectTrigger className="field-input !px-4" data-testid="artwork-availability-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-elevated border-line text-bone">
              <SelectItem value="original" data-testid="availability-original-option">
                Original Piece Only
              </SelectItem>
              <SelectItem value="prints" data-testid="availability-prints-option">
                Prints Available
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center justify-between border border-line bg-elevated px-4 py-3">
          <span className="font-meta text-[10px] uppercase tracking-[0.25em] text-mute">
            Feature on homepage
          </span>
          <Switch checked={form.featured} onCheckedChange={set("featured")} data-testid="artwork-featured-switch" />
        </div>
      </div>

      <button
        type="submit"
        disabled={busy || (!editing && !file && !libPath) || !form.title}
        data-testid="artwork-form-submit-button"
        className="mt-8 w-full border border-gold/60 bg-gold text-ink font-meta text-[11px] uppercase tracking-[0.3em] py-4 hover:bg-transparent hover:text-gold transition-colors duration-300 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {busy ? "Saving…" : editing ? "Save Changes" : "Add to Collection"}
      </button>
      {!editing && !file && !libPath && (
        <p className="font-meta text-[9px] uppercase tracking-[0.25em] text-mute mt-3 text-center">
          Add an image from your library or upload one to enable the button
        </p>
      )}
    </form>
  );
}
